import { Type, Eraser, Scissors, MousePointer, Languages } from 'lucide-react';
import type { ImageEditorTool } from '../types';

interface EditorToolsPanelProps {
  readonly selectedTool: ImageEditorTool;
  readonly onToolChange: (tool: ImageEditorTool) => void;
  readonly textSettings?: { color: string; fontSize: number } | undefined;
  readonly onTextSettingsChange?: ((settings: Partial<{ color: string; fontSize: number }>) => void) | undefined;
}

export const EditorToolsPanel = ({
  selectedTool,
  onToolChange,
  textSettings,
  onTextSettingsChange,
}: EditorToolsPanelProps) => {
  return (
    <div>
      {/* Tool Buttons - EDIT MODE Style */}
      <div className="d-flex flex-column gap-2 mb-3">
        <div
          onClick={() => onToolChange('select')}
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            background: selectedTool === 'select' ? '#e7f3ff' : 'transparent',
            border: selectedTool === 'select' ? '2px solid #0d6efd' : '2px solid transparent',
            transition: 'all 0.2s',
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <MousePointer size={18} color={selectedTool === 'select' ? '#0d6efd' : '#6c757d'} />
            <div>
              <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                Select
              </div>
              <small className="text-muted">Select and move elements</small>
            </div>
          </div>
        </div>

        <div
          onClick={() => onToolChange('rect-cutout')}
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            background: selectedTool === 'rect-cutout' ? '#e7f3ff' : 'transparent',
            border: selectedTool === 'rect-cutout' ? '2px solid #0d6efd' : '2px solid transparent',
            transition: 'all 0.2s',
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <Scissors size={18} color={selectedTool === 'rect-cutout' ? '#0d6efd' : '#6c757d'} />
            <div>
              <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                Cut Out Strip
              </div>
              <small className="text-muted">Remove horizontal strip</small>
            </div>
          </div>
        </div>

        <div
          onClick={() => onToolChange('erase')}
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            background: selectedTool === 'erase' ? '#e7f3ff' : 'transparent',
            border: selectedTool === 'erase' ? '2px solid #0d6efd' : '2px solid transparent',
            transition: 'all 0.2s',
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <Eraser size={18} color={selectedTool === 'erase' ? '#0d6efd' : '#6c757d'} />
            <div>
              <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                Erase Text
              </div>
              <small className="text-muted">Fill with background</small>
            </div>
          </div>
        </div>

        <div
          onClick={() => onToolChange('text')}
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            background: selectedTool === 'text' ? '#e7f3ff' : 'transparent',
            border: selectedTool === 'text' ? '2px solid #0d6efd' : '2px solid transparent',
            transition: 'all 0.2s',
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <Type size={18} color={selectedTool === 'text' ? '#0d6efd' : '#6c757d'} />
            <div>
              <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                Add Text
              </div>
              <small className="text-muted">Click on image to type</small>
            </div>
          </div>
        </div>

        <div
          onClick={() => onToolChange('translate')}
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            background: selectedTool === 'translate' ? '#e7f3ff' : 'transparent',
            border: selectedTool === 'translate' ? '2px solid #0d6efd' : '2px solid transparent',
            transition: 'all 0.2s',
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <Languages size={18} color={selectedTool === 'translate' ? '#0d6efd' : '#6c757d'} />
            <div>
              <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                Translate
              </div>
              <small className="text-muted">Translate with small area selection</small>
            </div>
          </div>
        </div>
      </div>

      {selectedTool === 'text' && (
        <div className="m-2">
          <div className="mb-2">
            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
              Text Color
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="color"
                value={textSettings?.color || '#000000'}
                onChange={(e) => {
                  if (onTextSettingsChange) {
                    onTextSettingsChange({ color: e.target.value });
                  }
                }}
                style={{
                  width: '50px',
                  height: '36px',
                  border: '2px solid #dee2e6',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              />
              <span style={{ fontSize: '0.75rem', color: '#6c757d', fontFamily: 'monospace' }}>
                {textSettings?.color || '#000000'}
              </span>
            </div>
          </div>

          <div className="mb-2">
            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
              Font Size
            </label>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[14, 18, 24, 32, 48].map((size) => (
                <button
                  key={size}
                  onClick={() => {
                    if (onTextSettingsChange) {
                      onTextSettingsChange({ fontSize: size });
                    }
                  }}
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    border: textSettings?.fontSize === size ? '2px solid #0d6efd' : '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: textSettings?.fontSize === size ? '#e7f3ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
